import { APICache } from './cache.js';
import { PreparedTrack, Track, TrackAlbum } from './container-classes.js';

const EMPTY_RESULT = { items: [], limit: 0, offset: 0, totalNumberOfItems: 0 };

function textFromRuns(runs = []) {
    return runs.map((run) => run.text || '').join('').trim();
}

function getBestThumbnail(thumbnails = []) {
    if (!Array.isArray(thumbnails) || thumbnails.length === 0) return null;
    return [...thumbnails].sort((a, b) => (b.width || 0) - (a.width || 0))[0]?.url || null;
}

function parseDuration(value) {
    if (!value || typeof value !== 'string' || !value.includes(':')) return 0;
    const parts = value
        .split(':')
        .map((part) => parseInt(part, 10))
        .filter((part) => Number.isFinite(part));
    return parts.reduce((total, part) => total * 60 + part, 0);
}

function findVideoId(value) {
    if (!value || typeof value !== 'object') return null;
    if (value.watchEndpoint?.videoId) return value.watchEndpoint.videoId;
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = findVideoId(item);
            if (found) return found;
        }
        return null;
    }
    for (const child of Object.values(value)) {
        const found = findVideoId(child);
        if (found) return found;
    }
    return null;
}

function findBrowseEndpoint(value, pageType) {
    if (!value || typeof value !== 'object') return null;
    if (
        value.browseEndpoint?.browseId &&
        (!pageType ||
            value.browseEndpoint?.browseEndpointContextSupportedConfigs?.browseEndpointContextMusicConfig?.pageType ===
                pageType)
    ) {
        return value.browseEndpoint;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = findBrowseEndpoint(item, pageType);
            if (found) return found;
        }
        return null;
    }
    for (const child of Object.values(value)) {
        const found = findBrowseEndpoint(child, pageType);
        if (found) return found;
    }
    return null;
}

function collectRenderers(value, key, output = []) {
    if (!value || typeof value !== 'object') return output;
    if (value[key]) output.push(value[key]);
    if (Array.isArray(value)) {
        value.forEach((item) => collectRenderers(item, key, output));
    } else {
        Object.values(value).forEach((item) => collectRenderers(item, key, output));
    }
    return output;
}

function isNormalizedSearchResult(data) {
    return data && typeof data === 'object' && data.tracks?.items && data.artists?.items && data.albums?.items;
}

export class YouTubeMusicAPI {
    constructor() {
        this.cache = new APICache({ maxSize: 200, ttl: 1000 * 60 * 20 });
        this.streamCache = new Map();
        this.artistCache = new Map();
    }

    async fetchJson(path, options = {}) {
        const response = await fetch(path, { signal: options.signal });
        if (!response.ok) {
            throw new Error(`YouTube Music request failed: ${response.status}`);
        }
        return response.json();
    }

    makeTrack(renderer) {
        const flexColumns = renderer.flexColumns || [];
        const titleRuns = flexColumns[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const metaRuns = flexColumns[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const title = textFromRuns(titleRuns);
        const videoId = findVideoId(renderer);
        if (!title || !videoId) return null;

        const metadata = metaRuns
            .map((run) => run.text)
            .filter((text) => text && text !== ' • ');
        const durationText = [...metadata].reverse().find((text) => /^\d+:\d{2}(?::\d{2})?$/.test(text));
        const artistName = metadata.find((text) => text !== durationText && !/views?$/i.test(text)) || 'YouTube Music';
        const artistEndpoint = findBrowseEndpoint(metaRuns, 'MUSIC_PAGE_TYPE_ARTIST');
        const thumbnails = renderer.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
        const cover = getBestThumbnail(thumbnails) || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

        const artist = {
            id: artistEndpoint?.browseId || `ytm-artist-${artistName}`,
            name: artistName,
            picture: cover,
            type: 'artist',
        };
        const album = new TrackAlbum({
            id: `y:${videoId}`,
            title: 'YouTube Music',
            cover,
            artist,
            artists: [artist],
        });

        return new PreparedTrack({
            id: `y:${videoId}`,
            provider: 'youtubeMusic',
            type: 'track',
            title,
            duration: parseDuration(durationText),
            artist,
            artists: [artist],
            album,
            audioQuality: 'HIGH',
            allowStreaming: true,
            streamReady: true,
            url: `https://music.youtube.com/watch?v=${videoId}`,
        });
    }

    makeArtist(renderer) {
        const titleRuns = renderer.flexColumns?.[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const name = textFromRuns(titleRuns);
        const browse = findBrowseEndpoint(renderer, 'MUSIC_PAGE_TYPE_ARTIST') || findBrowseEndpoint(renderer);
        if (!name || !browse?.browseId) return null;
        const thumbnails = renderer.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
        return {
            id: `y:${browse.browseId}`,
            provider: 'youtubeMusic',
            name,
            picture: getBestThumbnail(thumbnails),
            type: 'artist',
        };
    }

    makeAlbum(renderer) {
        const titleRuns = renderer.flexColumns?.[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const metaRuns = renderer.flexColumns?.[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const title = textFromRuns(titleRuns);
        const browse = findBrowseEndpoint(renderer, 'MUSIC_PAGE_TYPE_ALBUM') || findBrowseEndpoint(renderer);
        if (!title || !browse?.browseId) return null;
        const metadata = metaRuns.map((run) => run.text).filter((text) => text && text !== ' • ');
        const artistName = metadata.find((text) => !/^(album|single|ep)$/i.test(text) && !/^\d{4}$/.test(text)) || '';
        const thumbnails = renderer.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
        return {
            id: `y:${browse.browseId}`,
            provider: 'youtubeMusic',
            title,
            cover: getBestThumbnail(thumbnails),
            artist: artistName ? { id: `ytm-artist-${artistName}`, name: artistName } : null,
            artists: artistName ? [{ id: `ytm-artist-${artistName}`, name: artistName }] : [],
            numberOfTracks: 0,
            type: 'ALBUM',
        };
    }

    normalizeSearch(data) {
        if (isNormalizedSearchResult(data)) {
            return {
                tracks: data.tracks || { ...EMPTY_RESULT },
                videos: data.videos || { ...EMPTY_RESULT },
                artists: data.artists || { ...EMPTY_RESULT },
                albums: data.albums || { ...EMPTY_RESULT },
                playlists: data.playlists || { ...EMPTY_RESULT },
            };
        }

        const renderers = collectRenderers(data, 'musicResponsiveListItemRenderer');
        const tracks = [];
        const artists = new Map();
        const albums = [];

        for (const renderer of renderers) {
            const videoId = findVideoId(renderer);
            if (videoId) {
                const track = this.makeTrack(renderer);
                if (track) {
                    tracks.push(track);
                    if (track.artist?.id) artists.set(track.artist.id, track.artist);
                }
                continue;
            }

            const artist = this.makeArtist(renderer);
            if (artist) {
                artists.set(artist.id, artist);
                continue;
            }

            const album = this.makeAlbum(renderer);
            if (album) albums.push(album);
        }

        return {
            tracks: { ...EMPTY_RESULT, items: tracks, limit: tracks.length, totalNumberOfItems: tracks.length },
            videos: { ...EMPTY_RESULT },
            artists: {
                ...EMPTY_RESULT,
                items: Array.from(artists.values()),
                limit: artists.size,
                totalNumberOfItems: artists.size,
            },
            albums: { ...EMPTY_RESULT, items: albums, limit: albums.length, totalNumberOfItems: albums.length },
            playlists: { ...EMPTY_RESULT },
        };
    }

    async search(query, options = {}) {
        const cached = await this.cache.get('ytm_search', query);
        if (cached) return cached;
        const data = await this.fetchJson(`/api/ytmusic/search?q=${encodeURIComponent(query)}`, options);
        const result = this.normalizeSearch(data);
        await Promise.all(
            result.tracks.items.map((track) => this.cache.set('ytm_track', String(track.id).replace(/^y:/, ''), track))
        );
        await this.cache.set('ytm_search', query, result);
        return result;
    }

    async searchTracks(query, options = {}) {
        return (await this.search(query, options)).tracks;
    }

    async searchArtists(query, options = {}) {
        return (await this.search(query, options)).artists;
    }

    async searchAlbums(query, options = {}) {
        return (await this.search(query, options)).albums;
    }

    async searchPlaylists() {
        return { ...EMPTY_RESULT };
    }

    async searchVideos() {
        return { ...EMPTY_RESULT };
    }

    async getTrack(id) {
        const cleanId = String(id).replace(/^y:/, '');
        const cached = await this.cache.get('ytm_track', cleanId);
        if (cached) return { track: new Track(cached), info: { audioQuality: 'HIGH' } };
        const search = await this.search(cleanId);
        const track = search.tracks.items.find((item) => item.id === `y:${cleanId}`) || search.tracks.items[0];
        if (!track) throw new Error('YouTube Music track not found');
        const result = { track: new Track(track), info: { audioQuality: 'HIGH' } };
        await this.cache.set('ytm_track', cleanId, track);
        return result;
    }

    async getTrackMetadata(id) {
        return (await this.getTrack(id)).track;
    }

    async getStreamUrl(id) {
        const cleanId = String(id).replace(/^y:/, '');
        if (this.streamCache.has(cleanId)) return this.streamCache.get(cleanId);
        const data = await this.fetchJson(`/api/ytmusic/streams/${encodeURIComponent(cleanId)}`);
        if (!data.url) throw new Error('No playable YouTube Music stream found');
        const result = { url: `/api/ytmusic/audio/${encodeURIComponent(cleanId)}`, rgInfo: null };
        this.streamCache.set(cleanId, result);
        return result;
    }

    async getTrackRecommendations(id, limit = 25) {
        const cleanId = String(id).replace(/^y:/, '');
        if (!cleanId) return [];
        const data = await this.fetchJson(
            `/api/ytmusic/up-next/${encodeURIComponent(cleanId)}?limit=${encodeURIComponent(limit)}`
        );
        return Array.isArray(data.tracks) ? data.tracks : [];
    }

    async getRecommendedTracksForPlaylist(tracks, limit = 20, options = {}) {
        const knownTrackIds = options.knownTrackIds || new Set();
        const seeds = [...(tracks || [])]
            .reverse()
            .filter((track) => String(track?.id || '').startsWith('y:'))
            .slice(0, 3);
        if (seeds.length === 0) return [];

        const recommendationSets = await Promise.allSettled(
            seeds.map((seed) => this.getTrackRecommendations(seed.id, Math.max(limit, 12)))
        );
        const merged = [];
        const seen = new Set();
        for (const result of recommendationSets) {
            if (result.status !== 'fulfilled') continue;
            for (const track of result.value) {
                if (!track?.id || seen.has(track.id) || knownTrackIds.has(track.id)) continue;
                seen.add(track.id);
                merged.push(track);
            }
        }
        return merged.slice(0, limit * 2);
    }

    getCoverUrl(id) {
        if (!id) return 'assets/appicon.png';
        return String(id);
    }

    getCoverSrcset() {
        return '';
    }

    getVideoCoverUrl(id) {
        return this.getCoverUrl(id);
    }

    getArtistPictureUrl(id) {
        return this.getCoverUrl(id);
    }

    getArtistPictureSrcset() {
        return '';
    }

    async getAlbum(id) {
        const cleanId = String(id).replace(/^y:/, '').replace(/^ytm-album-/, '');
        if (/^MPRE/.test(cleanId)) {
            return this.fetchJson(`/api/ytmusic/albums/${encodeURIComponent(cleanId)}`);
        }
        const { track } = await this.getTrack(cleanId);
        return {
            album: {
                ...track.album,
                numberOfTracks: 1,
                duration: track.duration || 0,
            },
            tracks: [new Track(track)],
        };
    }

    async getArtist(id) {
        const name = decodeURIComponent(String(id).replace(/^y:/, '').replace(/^ytm-artist-/, ''));
        if (/^(UC|FEmusic_|MPLA|MPRE|VL)/.test(name)) {
            const artist = await this.fetchJson(`/api/ytmusic/artists/${encodeURIComponent(name)}`);
            this.artistCache.set(name, artist);
            return artist;
        }
        const search = await this.search(name);
        const artist =
            search.artists.items.find((item) => item.id === id || item.name.toLowerCase() === name.toLowerCase()) ||
            search.artists.items[0] || { id, name, picture: null };
        const realArtistId = String(artist.id || '').replace(/^y:/, '');
        if (/^(UC|FEmusic_|MPLA|MPRE|VL)/.test(realArtistId)) {
            const artistPage = await this.fetchJson(`/api/ytmusic/artists/${encodeURIComponent(realArtistId)}`);
            this.artistCache.set(realArtistId, artistPage);
            this.artistCache.set(name, artistPage);
            return artistPage;
        }
        return {
            ...artist,
            popularity: 0,
            artistRoles: [],
            tracks: search.tracks.items,
            albums: [],
            eps: [],
            videos: [],
            mixes: {},
        };
    }

    async getArtistTopTracks(id, options = {}) {
        const artist = await this.getArtist(id);
        return (artist.tracks || []).slice(0, options.limit || 25);
    }

    async getArtistBiography() {
        return null;
    }

    async getSimilarArtists(id) {
        const cleanId = String(id).replace(/^y:/, '').replace(/^ytm-artist-/, '');
        const artist = this.artistCache.get(cleanId) || (await this.getArtist(cleanId));
        return artist.similarArtists || [];
    }

    async getSimilarAlbums() {
        return [];
    }

    async clearCache() {
        await this.cache.clear();
        this.streamCache.clear();
    }

    getCacheStats() {
        return {
            youtubeMusicCache: this.cache.getCacheStats(),
            youtubeMusicStreams: this.streamCache.size,
        };
    }
}
